import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable, tap } from 'rxjs'
import { environment } from '../../../environments/environment'
import { Role } from '../../shared/constants/enums'
import { AuthorizeRepresentativeDto, RepresentativeDto } from '../../shared/models/auth.interfaces'
import { RedirectRequestDto } from '../../shared/models/guarantee-order.interfaces'
import { AppLanguageService } from './app-language.service'

@Injectable({ providedIn: 'root' })
export class AuthService {
    currentRepresentative = new BehaviorSubject<RepresentativeDto | null>(null)
    currentRepresentativeAccessToken = new BehaviorSubject<string | null>(null)
    currentRepresentativeRefreshToken = new BehaviorSubject<string | null>(null)

    public refreshTokenUrl: string = `${environment.refreshTokenUrl}`
    private currentRepresentativeStorageItemId: string = 'currentRepresentative'
    private currentRepresentativeAccessTokenStorageItemId: string = 'currentRepresentativeAccessToken'
    private currentRepresentativeRefreshTokenStorageItemId: string = 'currentRepresentativeRefreshToken'

    constructor(
        private http: HttpClient,
        private appLanguageService: AppLanguageService
    ) {}

    login(token: string): void {
        const authorizeRepresentativeDto: AuthorizeRepresentativeDto = JSON.parse(this.b64DecodeWithUnicode(token))
        this.setCurrentUserDataAndTokens(authorizeRepresentativeDto)
        this.appLanguageService.setAppLanguage(authorizeRepresentativeDto.language)
    }

    autoLogin(): void {
        const currentRepresentativeRefreshToken = localStorage.getItem(
            this.currentRepresentativeRefreshTokenStorageItemId
        )
        const currentRepresentativeAccessToken = localStorage.getItem(
            this.currentRepresentativeAccessTokenStorageItemId
        )
        const currentRepresentative = localStorage.getItem(this.currentRepresentativeStorageItemId)
        if (!currentRepresentativeAccessToken || !currentRepresentative) {
            return
        }
        this.currentRepresentativeAccessToken.next(currentRepresentativeAccessToken)
        this.currentRepresentativeRefreshToken.next(currentRepresentativeRefreshToken)
        this.currentRepresentative.next(JSON.parse(currentRepresentative))
    }

    refreshAuthorization$(): Observable<AuthorizeRepresentativeDto> {
        return this.refreshToken().pipe(
            tap((authorizeRepresentativeDto: AuthorizeRepresentativeDto) => {
                this.setCurrentUserDataAndTokens(authorizeRepresentativeDto)
            })
        )
    }

    logout(): Observable<RedirectRequestDto> {
        return this.http.get<RedirectRequestDto>(`${environment.backendUrl}/logout`).pipe(
            tap(() => {
                localStorage.removeItem(this.currentRepresentativeAccessTokenStorageItemId)
                localStorage.removeItem(this.currentRepresentativeStorageItemId)
                localStorage.removeItem(this.currentRepresentativeRefreshTokenStorageItemId)
                this.appLanguageService.removeAppLanguage()
                this.currentRepresentativeAccessToken.next(null)
                this.currentRepresentativeRefreshToken.next(null)
                this.currentRepresentative.next(null)
            })
        )
    }

    getRepresentativeRole(): Role | null {
        if (null != this.currentRepresentative.value) {
            return this.currentRepresentative.value?.role
        }
        return null
    }

    private setCurrentUserDataAndTokens(authorizeRepresentativeDto: AuthorizeRepresentativeDto): void {
        this.currentRepresentativeAccessToken.next(authorizeRepresentativeDto.accessToken)
        this.currentRepresentativeRefreshToken.next(authorizeRepresentativeDto.refreshToken)
        this.currentRepresentative.next(authorizeRepresentativeDto.representative)
        localStorage.setItem(this.currentRepresentativeAccessTokenStorageItemId, authorizeRepresentativeDto.accessToken)
        localStorage.setItem(
            this.currentRepresentativeRefreshTokenStorageItemId,
            authorizeRepresentativeDto.refreshToken
        )
        localStorage.setItem(
            this.currentRepresentativeStorageItemId,
            JSON.stringify(authorizeRepresentativeDto.representative)
        )
    }

    private refreshToken(): Observable<AuthorizeRepresentativeDto> {
        const headers = new Headers()
        headers.append('Authorization', this.currentRepresentativeRefreshToken.value!)
        return this.http.post<AuthorizeRepresentativeDto>(this.refreshTokenUrl, null, {
            headers: {
                Authorization: this.currentRepresentativeRefreshToken.value!
            }
        })
    }

    private b64DecodeWithUnicode(tokenString: string): any {
        return decodeURIComponent(
            Array.prototype.map
                .call(atob(tokenString), function (c) {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
                })
                .join('')
        )
    }
}
